/**
 * UJ Localization Panel - Confirm & Switch Experience
 * Implements a two-view localization panel for market and language switching
 * 
 * Features:
 * - Multi-language support per market
 * - Browser language detection
 * - Auto-show on first visit
 * - 180-day persistence cookie
 */

(function(window) {
  'use strict';

  // Configuration
  const CONFIG = {
    cookieName: 'uj_locale_v3',
    cookieDays: 180,
    dismissalCountKey: 'uj_locale_dismissals',
    panelId: 'uj-localization-panel',
    dataId: 'uj-localization-data',
    currentDataId: 'uj-localization-current',
    marketLocalesId: 'uj-localization-market-locales',
    allLocalesId: 'uj-localization-all-locales',
    autoShowDelay: 1000,
    pendingLocaleKey: 'uj_pending_locale',
    continentMap: {
      'AF': 'AFRICA',
      'AN': 'OCEANIA',
      'AS': 'ASIA',
      'EU': 'EUROPE',
      'NA': 'AMERICAS',
      'OC': 'OCEANIA',
      'SA': 'AMERICAS',
      'AFRICA': 'AFRICA',
      'AFRIQUE': 'AFRICA',
      'AFRIKA': 'AFRICA',
      'AMERICAS': 'AMERICAS',
      'AMERIQUES': 'AMERICAS',
      'AMERIQUE': 'AMERICAS',
      'ANTARCTICA': 'OCEANIA',
      'ASIA': 'ASIA',
      'ASIE': 'ASIA',
      'ASIEN': 'ASIA',
      'EUROPE': 'EUROPE',
      'EUROPA': 'EUROPE',
      'NORTH AMERICA': 'AMERICAS',
      'NORTH AMERICA': 'AMERICAS',
      'SOUTH AMERICA': 'AMERICAS',
      'SOUTH AMERICA': 'AMERICAS',
      'OCEANIA': 'OCEANIA',
      'OCEANIE': 'OCEANIA',
      'OZEANIEN': 'OCEANIA'
    },
    countryOrder: {
      AMERICAS: ['US', 'CA', 'MX'],
      EUROPE: ['GB', 'FR', 'DE', 'NL', 'ES', 'IT'],
      ASIA: [],
      AFRICA: [],
      OCEANIA: []
    }
  };

  // Browser Language Manager
  class BrowserLanguageManager {
    constructor() {
      this.browserLanguage = null;
      this.init();
    }

    init() {
      // Get browser language
      this.browserLanguage = this.getBrowserLanguage();
    }

    getBrowserLanguage() {
      // Get browser language code (e.g., 'en' from 'en-US')
      const lang = navigator.language || navigator.userLanguage || 'en';
      return lang.split('-')[0].toLowerCase();
    }
  }

  // Market Data Manager
  class MarketDataManager {
    constructor() {
      this.markets = [];
      this.marketLocales = {};
      this.allLocales = [];
      this.currentMarket = null;
      this.currentLocale = null;
      this.suggestedMarket = null;
      this.suggestedLocale = null;
      this.init();
    }

    init() {
      try {
        // Load markets data from the stable Shopify market master.
        const marketsEl = document.getElementById(CONFIG.dataId);
        if (marketsEl) {
          this.markets = JSON.parse(marketsEl.textContent) || [];
        }

        // Load current market/locale
        const currentEl = document.getElementById(CONFIG.currentDataId);
        if (currentEl) {
          const data = JSON.parse(currentEl.textContent);
          this.currentMarket = data.market;
          this.currentLocale = data.locale;
        }

        // Load market-specific locales
        const marketLocalesEl = document.getElementById(CONFIG.marketLocalesId);
        if (marketLocalesEl) {
          this.marketLocales = JSON.parse(marketLocalesEl.textContent);
        }

        // Load the store-wide locale list so domain-level markets can be identified
        const allLocalesEl = document.getElementById(CONFIG.allLocalesId);
        if (allLocalesEl) {
          this.allLocales = JSON.parse(allLocalesEl.textContent);
        }
      } catch (e) {
        console.error('Error loading localization data:', e);
      }
    }

    getMarketByIso(iso) {
      return this.markets.find(m => m.iso_code === iso);
    }

    getLocalesForMarket(marketIso) {
      return this.marketLocales[marketIso] || [];
    }

    getDisplayLocalesForMarket(marketIso) {
      const locales = this.getLocalesForMarket(marketIso);
      const defaultLocale = this.getDefaultLocale(marketIso);

      if (!Array.isArray(locales) || !locales.length) return [defaultLocale];

      const currentMarketIso = this.getSelectedMarket()?.iso_code || this.currentMarket?.iso_code;
      const isActiveMarket = marketIso && currentMarketIso && marketIso === currentMarketIso;

      // Shopify locale paths use the language code as the active locale, while
      // the market code is the country code. Compare against the market's
      // configured locales instead of comparing the path suffix to the market ISO.
      const activeLocaleCode = this.getActiveLocaleCode();

      // For a market with no real locale config, only the default language should be shown.
      const hasExplicitLocaleConfig = Array.isArray(this.marketLocales[marketIso]) && this.marketLocales[marketIso].length > 0;
      if (!hasExplicitLocaleConfig) {
        return [defaultLocale];
      }

      if (isActiveMarket) {
        const normalizedMarketLocales = locales.map(locale => this.normalizeLocale(locale.iso_code));
        return normalizedMarketLocales.includes(activeLocaleCode) ? locales : [defaultLocale];
      }

      // Explicit market locale configuration is authoritative. Do not collapse
      // a configured market such as Canada to its default language merely because
      // its locales match the store-wide locale list.
      return locales;
    }

    getDefaultLocale(marketIso) {
      const locales = this.getLocalesForMarket(marketIso);
      return locales.find(locale => this.normalizeLocale(locale.iso_code) === 'en') || locales[0] || {
        iso_code: 'en',
        name: 'English',
        label: 'English'
      };
    }

    getEnglishLocale() {
      return this.allLocales.find(locale => this.normalizeLocale(locale.iso_code) === 'en') || {
        iso_code: 'en',
        name: 'English',
        label: 'English'
      };
    }

    getLocaleLabel(locale) {
      const globalLocale = this.allLocales.find(item =>
        this.normalizeLocale(item.iso_code) === this.normalizeLocale(locale?.iso_code)
      );

      // Display the stable English language name in the selector. Shopify's
      // localized endonym can be identical for both language options, such as
      // "Deutsch" for German and English in the German market.
      return [locale?.name, globalLocale?.name, locale?.label, globalLocale?.label, locale?.iso_code]
        .find(label => typeof label === 'string' && label.trim()) || '';
    }

    getPreferredLocale(marketIso, browserLanguage) {
      const locales = this.getDisplayLocalesForMarket(marketIso);
      return locales.find(locale => this.normalizeLocale(locale.iso_code) === browserLanguage) ||
        locales[0] ||
        this.getDefaultLocale(marketIso);
    }

    normalizeLocale(locale) {
      return String(locale || '').toLowerCase().split('-')[0];
    }

    normalizeContinentKey(value) {
      if (!value) return '';
      if (typeof value === 'object') {
        return this.normalizeContinentKey(value.code || value.name || value.label || value.iso_code || '');
      }

      let normalized = String(value)
        .trim()
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^A-Z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      const aliasMap = {
        'AMERIKA': 'AMERICAS',
        'AMERIQUES': 'AMERICAS',
        'AMERIQUE': 'AMERICAS',
        'EUROPA': 'EUROPE',
        'AZIE': 'ASIA',
        'ASIEN': 'ASIA',
        'ASIE': 'ASIA',
        'AFRIQUE': 'AFRICA',
        'AFRIKA': 'AFRICA',
        'OCEANIE': 'OCEANIA',
        'OZEANIEN': 'OCEANIA'
      };

      return aliasMap[normalized] || normalized;
    }

    getActiveLocaleCode() {
      const localePath = window.location.pathname.match(/^\/([a-z]{2})(?:-[a-z]{2})?(?:\/|$)/i);
      return this.normalizeLocale(
        localePath?.[1] || document.documentElement.lang || this.currentLocale?.iso_code
      );
    }

    setSuggestedLocation(countryIso, browserLanguage) {
      const market = this.getMarketByIso(countryIso);
      if (!market) return;

      this.suggestedMarket = market;
      this.suggestedLocale = this.getPreferredLocale(market.iso_code, browserLanguage);
    }

    getSelectedMarket() {
      // The actual current market on the page is the source of truth. A stale
      // geo suggestion should not override the real market after a locale switch.
      return this.currentMarket || this.suggestedMarket;
    }

    getSelectedLocale() {
      return this.currentLocale || this.suggestedLocale;
    }

    mapContinentKey(continentValue) {
      const normalized = this.normalizeContinentKey(continentValue);
      if (!normalized) return 'AMERICAS';

      return CONFIG.continentMap[normalized] ||
        (normalized === 'NORTH AMERICA' || normalized === 'SOUTH AMERICA' || normalized === 'NA' || normalized === 'SA' ? 'AMERICAS' :
          (normalized === 'EU' || normalized === 'EUROPE' ? 'EUROPE' :
            (normalized === 'AS' || normalized === 'ASIA' ? 'ASIA' :
              (normalized === 'AF' || normalized === 'AFRICA' ? 'AFRICA' :
                (normalized === 'OC' || normalized === 'OCEANIA' ? 'OCEANIA' : 'AMERICAS')))));
    }

    getContinentForMarket(marketIso) {
      const market = this.getMarketByIso(marketIso);
      if (!market) return 'AMERICAS';

      const continentValue = market.continent_code || market.continent || market.continent?.code || market.continent?.name || '';
      return this.mapContinentKey(continentValue);
    }

    getMarketsGroupedByContinent(excludedMarketIso) {
      const grouped = {};

      // Use the global Shopify market list as the only source of truth.
      // Sort by English country names so translated display names keep the same order on every locale.
      const englishCountryNames = typeof Intl.DisplayNames === 'function'
        ? new Intl.DisplayNames(['en'], { type: 'region' })
        : null;
      const getStableCountryName = market =>
        englishCountryNames?.of(market.iso_code) || market.iso_code || market.name;
      const sortedMarkets = [...this.markets].sort((a, b) =>
        getStableCountryName(a).localeCompare(getStableCountryName(b), 'en')
      );

      sortedMarkets.forEach(market => {
        const continentValue = market.continent_code || market.continent || market.continent?.code || market.continent?.name || '';
        const continent = this.mapContinentKey(continentValue);

        if (!grouped[continent]) {
          grouped[continent] = [];
        }

        grouped[continent].push(market);
      });

      Object.keys(grouped).forEach(continent => {
        const priorityOrder = CONFIG.countryOrder[continent] || [];
        grouped[continent].sort((a, b) => {
          const aPriority = priorityOrder.indexOf(String(a.iso_code || '').toUpperCase());
          const bPriority = priorityOrder.indexOf(String(b.iso_code || '').toUpperCase());

          const aRank = aPriority === -1 ? priorityOrder.length : aPriority;
          const bRank = bPriority === -1 ? priorityOrder.length : bPriority;

          if (aRank !== bRank) return aRank - bRank;
          return getStableCountryName(a).localeCompare(getStableCountryName(b), 'en');
        });
      });

      return Object.fromEntries(
        Object.entries(grouped).filter(([, markets]) => Array.isArray(markets) && markets.length)
      );
    }

    getTotalMarkets() {
      return this.markets.length;
    }

    getTotalLocales() {
      // Count unique locales across all markets
      const uniqueLocales = new Set();
      Object.values(this.marketLocales).forEach(locales => {
        locales.forEach(locale => {
          uniqueLocales.add(locale.iso_code);
        });
      });
      return uniqueLocales.size;
    }

    getLocalesForCurrentMarket() {
      if (!this.currentMarket) return [];
      return this.getLocalesForMarket(this.currentMarket.iso_code);
    }
  }

  // Cookie Manager
  class CookieManager {
    static set(name, value, days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      const expires = 'expires=' + date.toUTCString();
      document.cookie = `${name}=${value};${expires};path=/`;
    }

    static get(name) {
      const nameEQ = name + '=';
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        cookie = cookie.trim();
        if (cookie.indexOf(nameEQ) === 0) {
          return cookie.substring(nameEQ.length);
        }
      }
      return null;
    }

    static delete(name) {
      this.set(name, '', -1);
    }

    static exists(name) {
      return this.get(name) !== null;
    }
  }

  // Translation Manager
  class TranslationManager {
    constructor() {
      this.translations = {};
      this.currentLanguage = document.documentElement.lang || 'en';
      this.loadTranslations();
    }

    loadTranslations() {
      // Try to load translations from page data or use defaults
      try {
        // Access translations from Shopify's Liquid data if available
        const translationScript = document.querySelector('[data-shopify-i18n]');
        if (translationScript) {
          this.translations = JSON.parse(translationScript.textContent);
        }
      } catch (e) {
        // Fall back to defaults
        this.translations = this.getDefaultTranslations();
      }
    }

    t(key, defaultText = '') {
      const keys = key.split('.');
      let value = this.translations;
      
      for (const k of keys) {
        value = value[k];
        if (value === undefined) {
          return defaultText || key;
        }
      }
      
      return value;
    }

    getDefaultTranslations() {
      return {
        localization: {
          confirm: {
            location_label: 'Your Location',
            delivery_note: 'Delivery within {{ country }}',
            cta: 'Continue to {{ country }}',
            change_multi: 'Change Country and Language',
            change_single: 'Change Country and Language',
            metadata_multi: '{{ languages }} languages · {{ countries }} countries'
          },
          location: {
            title: 'Location',
            warning: 'Changing your country will empty your cart.',
            all_countries: 'All Countries',
            back: '← Back'
          }
        }
      };
    }
  }

  // Panel Manager
  class LocalizationPanel {
    constructor(dataManager, translationManager, browserLanguageManager) {
      this.dataManager = dataManager;
      this.translationManager = translationManager;
      this.browserLanguageManager = browserLanguageManager;
      this.currentView = 'confirm';
      this.isOpen = false;
      this.panelEl = null;
      this.init();
    }

    init() {
      this.createPanelHTML();
      this.bindEvents();
    }

    createPanelHTML() {
      const panelHTML = `
        <div id="${CONFIG.panelId}" class="uj-localization-panel" role="dialog" aria-labelledby="uj-localization-title">
          <div class="uj-localization-panel__overlay"></div>
          <div class="uj-localization-panel__content">
            <button class="uj-localization-panel__close" aria-label="Close localization panel">×</button>
            
            <!-- Confirm View -->
            <div class="uj-localization-panel__view uj-localization-confirm" data-view="confirm">
              <h2 id="uj-localization-title" class="uj-localization-confirm__title">YOUR LOCATION</h2>
              
              <div class="uj-localization-confirm__market">
                <p class="uj-localization-confirm__country-name" id="uj-confirm-country"></p>
              </div>

              <div class="uj-localization-confirm__details">
                <p class="uj-localization-confirm__language-currency">
                  <span id="uj-confirm-language"></span>
                  <span class="uj-localization-confirm__separator">·</span>
                  <span id="uj-confirm-currency"></span>
                </p>
                <p class="uj-localization-confirm__delivery" id="uj-confirm-delivery"></p>
              </div>

              <button class="uj-localization-panel__cta uj-localization-panel__cta--primary" id="uj-confirm-continue">
                Continue to <span id="uj-confirm-continue-country"></span>
              </button>

              <button class="uj-localization-confirm__change-location" id="uj-confirm-change">
                <span id="uj-confirm-change-label"></span>
                <span class="uj-localization-confirm__change-arrow">→</span>
              </button>

            </div>

            <!-- Location View -->
            <div class="uj-localization-panel__view uj-localization-location" data-view="location" style="display: none;">
              <button class="uj-localization-location__back" id="uj-location-back">
                <span class="uj-localization-location__back-arrow">←</span>
                <span>Back</span>
              </button>

              <h2 class="uj-localization-location__title">Location</h2>

              <p class="uj-localization-location__warning">Changing your country will empty your cart.</p>

              <!-- Current Market Languages (pinned) -->
              <div class="uj-localization-location__current-market" id="uj-location-current-market" style="display: none;">
                <p class="uj-localization-location__market-label" id="uj-location-current-label"></p>
                <div class="uj-localization-location__languages" id="uj-location-current-languages"></div>
              </div>

              <!-- All Other Countries -->
              <div class="uj-localization-location__all-countries">
                <p class="uj-localization-location__all-label">All Countries</p>
                <div class="uj-localization-location__continents" id="uj-location-continents"></div>
              </div>
            </div>
          </div>
        </div>
      `;

      const container = document.createElement('div');
      container.innerHTML = panelHTML;
      this.panelEl = container.firstElementChild;
      document.body.appendChild(this.panelEl);
    }

    // Add these if you want to display metadata about the number of languages and countries available
    // <p class="uj-localization-confirm__metadata" id="uj-confirm-metadata" aria-describedby="uj-confirm-metadata"></p>
    
    bindEvents() {
      // Close button
      this.panelEl.querySelector('.uj-localization-panel__close').addEventListener('click', () => this.close());

      // Overlay click
      this.panelEl.querySelector('.uj-localization-panel__overlay').addEventListener('click', () => this.close());

      // Continue button
      document.getElementById('uj-confirm-continue').addEventListener('click', () => this.continueWithSelection());

      // Change location button
      document.getElementById('uj-confirm-change').addEventListener('click', () => this.showLocationView());

      // Back button
      document.getElementById('uj-location-back').addEventListener('click', () => this.showConfirmView());

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });
    }

    updateConfirmView() {
      const country = this.dataManager.getSelectedMarket();
      const locale = this.dataManager.getSelectedLocale();
      const localesForMarket = country ? this.dataManager.getLocalesForMarket(country.iso_code) : [];

      if (!country) return;

      // Update country name
      document.getElementById('uj-confirm-country').textContent = country.name;
      document.getElementById('uj-confirm-continue-country').textContent = country.name;

      // Update language and currency
      const langEl = document.getElementById('uj-confirm-language');
      const currEl = document.getElementById('uj-confirm-currency');

      langEl.textContent = locale?.label || locale?.name || 'English';
      currEl.textContent = country.currency.symbol;

      // Update delivery message
      const deliveryEl = document.getElementById('uj-confirm-delivery');
      deliveryEl.textContent = `Delivery within ${country.name}`;

      // Update change label based on locale count
      const changeLabelEl = document.getElementById('uj-confirm-change-label');
      const hasMultipleLocales = localesForMarket.length > 1;
      changeLabelEl.textContent = hasMultipleLocales 
        ? 'Change Country and Language' 
        : 'Change Country and Language';

      // Update metadata with actual counts
      const metadataEl = document.getElementById('uj-confirm-metadata');
      const totalLocales = this.dataManager.getTotalLocales();
      const totalMarkets = this.dataManager.getTotalMarkets();
      
      if (hasMultipleLocales) {
        metadataEl.textContent = `${totalLocales} languages · ${totalMarkets} countries`;
      } else {
        metadataEl.textContent = `${totalMarkets} countries`;
      }
    }

    updateLocationView() {
      const currentMarket = this.dataManager.currentMarket;
      const selectedMarket = this.dataManager.getSelectedMarket() || currentMarket;
      const activeLocaleCode = this.dataManager.getActiveLocaleCode();
      const localesForMarket = selectedMarket ? this.dataManager.getDisplayLocalesForMarket(selectedMarket.iso_code) : [];
      
      // Show current market languages if multi-language
      if (localesForMarket.length > 1) {
        const marketLabelEl = document.getElementById('uj-location-current-label');
        marketLabelEl.textContent = `${selectedMarket.name.toUpperCase()} · PRICES STAY IN ${selectedMarket.currency.symbol}`;

        const languagesContainer = document.getElementById('uj-location-current-languages');
        languagesContainer.innerHTML = localesForMarket.map(locale => `
          <button class="uj-localization-location__language" 
                  data-market="${selectedMarket.iso_code}"
                  data-locale="${locale.iso_code}"
                  ${this.dataManager.normalizeLocale(locale.iso_code) === activeLocaleCode ? 'aria-current="true"' : ''}>
            <span class="uj-localization-location__language-name">${selectedMarket.name} (<span translate="no">${this.dataManager.getLocaleLabel(locale)}</span>)</span>
            <span class="uj-localization-location__language-currency">${selectedMarket.currency.symbol}</span>
          </button>
        `).join('');

        // Bind language selection
        languagesContainer.querySelectorAll('.uj-localization-location__language').forEach(btn => {
          btn.addEventListener('click', (e) => this.selectLanguage(e));
        });

        document.getElementById('uj-location-current-market').style.display = 'block';
      } else {
        document.getElementById('uj-location-current-market').style.display = 'none';
      }

      // Show all countries grouped by continent, including the currently detected
      // market so the user can see it in the selection list before continuing.
      const groupedMarkets = this.dataManager.getMarketsGroupedByContinent(null);
      const continentsContainer = document.getElementById('uj-location-continents');

      continentsContainer.innerHTML = '';

      const continentOrder = ['AMERICAS', 'EUROPE', 'ASIA', 'AFRICA', 'OCEANIA'];
      const sortedContinents = Object.keys(groupedMarkets).sort((a, b) => {
        const aIndex = continentOrder.indexOf(a);
        const bIndex = continentOrder.indexOf(b);
        return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
      });

      sortedContinents.forEach(continent => {
        const markets = groupedMarkets[continent];
        const continentId = `continent-${continent.toLowerCase()}`;

        const continentHTML = `
          <div class="uj-localization-location__continent">
            <button class="uj-localization-location__continent-toggle"
                    aria-expanded="false"
                    aria-controls="${continentId}">
              ${continent}
              <span class="uj-localization-location__continent-toggle-icon" aria-hidden="true">
                ${this.getContinentToggleIconMarkup(false)}
              </span>
            </button>
            <div class="uj-localization-location__countries" id="${continentId}"
                 style="display: none;">
              ${markets.map(market => this.getMarketLocaleRows(market, selectedMarket)).join('')}
            </div>
          </div>
        `;

        continentsContainer.insertAdjacentHTML('beforeend', continentHTML);
      });

      // Bind continent toggle events
      continentsContainer.querySelectorAll('.uj-localization-location__continent-toggle').forEach(toggle => {
        toggle.addEventListener('click', (e) => this.toggleContinent(e));
      });

      // Bind country selection events
      continentsContainer.querySelectorAll('.uj-localization-location__country').forEach(country => {
        country.addEventListener('click', (e) => this.selectMarket(e));
      });
    }

    getMarketLanguageMeta(market) {
      const locale = this.dataManager.getDefaultLocale(market.iso_code);

      return `(${this.dataManager.normalizeLocale(locale.iso_code).toUpperCase()}) · ${market.currency.symbol}`;
    }

    getMarketLocaleRows(market, selectedMarket) {
      const defaultLocale = this.dataManager.getDefaultLocale(market.iso_code);
      const locales = this.dataManager.getDisplayLocalesForMarket(market.iso_code);
      const marketLocales = locales.length ? locales : [defaultLocale];

      return marketLocales.map(locale => `
        <button class="uj-localization-location__country"
                data-market="${market.iso_code}"
                data-locale="${locale.iso_code}"
                ${market.iso_code === selectedMarket.iso_code ? 'aria-current="true"' : ''}>
          <span class="uj-localization-location__country-name">${market.name}</span>
          <span class="uj-localization-location__country-meta">(<span class="uj-localization-location__locale-code" data-locale-code="${this.dataManager.normalizeLocale(locale.iso_code).toUpperCase()}" translate="no"></span>) · ${market.currency.symbol}</span>
        </button>
      `).join('');
    }

    getContinentToggleIconMarkup(isExpanded) {
      return isExpanded ? `
        <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" focusable="false">
          <path d="M4 10H16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
      ` : `
        <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" focusable="false">
          <path d="M10 4V16M4 10H16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
      `;
    }

    toggleContinent(e) {
      e.preventDefault();
      const button = e.currentTarget;
      const isExpanded = button.getAttribute('aria-expanded') === 'true';
      const controlsId = button.getAttribute('aria-controls');
      const countriesDiv = document.getElementById(controlsId);

      // Close other continents
      document.querySelectorAll('.uj-localization-location__continent-toggle').forEach(toggle => {
        if (toggle !== button) {
          toggle.setAttribute('aria-expanded', 'false');
          const icon = toggle.querySelector('.uj-localization-location__continent-toggle-icon');
          if (icon) icon.innerHTML = this.getContinentToggleIconMarkup(false);
          const otherId = toggle.getAttribute('aria-controls');
          document.getElementById(otherId).style.display = 'none';
        }
      });

      // Toggle current continent
      const willExpand = !isExpanded;
      button.setAttribute('aria-expanded', willExpand);
      const icon = button.querySelector('.uj-localization-location__continent-toggle-icon');
      if (icon) icon.innerHTML = this.getContinentToggleIconMarkup(willExpand);
      countriesDiv.style.display = isExpanded ? 'none' : 'flex';
    }

    selectLanguage(e) {
      e.preventDefault();
      const button = e.currentTarget;
      const marketIso = button.getAttribute('data-market');
      const localeIso = button.getAttribute('data-locale');

      // Language-only switch within same market
      this.switchLanguage(marketIso, localeIso);
    }

    selectMarket(e) {
      e.preventDefault();
      const button = e.currentTarget;
      const marketIso = button.getAttribute('data-market');
      const localeIso = button.getAttribute('data-locale');

      // Different market - switch country
      this.switchMarket(marketIso, localeIso);
    }

    switchLanguage(marketIso, localeIso) {
      // Switch language within same market - don't clear cart
      const form = document.querySelector('.localization-form');
      if (!form) return;
      const countryInput = form.querySelector('input[name="country_code"]');
      const languageInput = form.querySelector('input[name="language_code"]');

      if (countryInput && languageInput) {
        countryInput.value = marketIso;
        languageInput.value = localeIso;
        this.rememberPendingLocale(marketIso, localeIso);
        if (form.submit) {
          form.submit();
        }
      }
    }

    switchMarket(marketIso, localeIso) {
      // Shopify changes the market but does not clear the existing cart.
      const form = document.querySelector('.localization-form');
      if (!form) return;
      const countryInput = form.querySelector('input[name="country_code"]');
      const languageInput = form.querySelector('input[name="language_code"]');
      const selectedLocale = localeIso
        ? this.dataManager.getDisplayLocalesForMarket(marketIso).find(locale => locale.iso_code === localeIso)
        : this.dataManager.getPreferredLocale(marketIso, this.browserLanguageManager.browserLanguage);

      if (countryInput) {
        countryInput.value = marketIso;
        if (languageInput && selectedLocale) {
          languageInput.value = selectedLocale.iso_code;
        }

        this.rememberPendingLocale(marketIso, selectedLocale?.iso_code || 'en');

        fetch('/cart/clear.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin'
        })
          .then(response => {
            if (!response.ok) throw new Error('Cart could not be cleared');
            return response;
          })
          .then(() => {
            if (form.submit) form.submit();
          })
          .catch(() => {
            this.clearPendingLocale();
          });
      }
    }

    rememberPendingLocale(marketIso, localeIso) {
      window.sessionStorage.setItem(
        CONFIG.pendingLocaleKey,
        JSON.stringify({ marketIso, localeIso })
      );
    }

    clearPendingLocale() {
      window.sessionStorage.removeItem(CONFIG.pendingLocaleKey);
    }

    confirmPendingLocale() {
      try {
        const pending = JSON.parse(window.sessionStorage.getItem(CONFIG.pendingLocaleKey));
        if (!pending) return;

        const activeLocale = this.dataManager.getActiveLocaleCode();
        if (
          this.dataManager.currentMarket?.iso_code === pending.marketIso &&
          this.dataManager.normalizeLocale(pending.localeIso) === activeLocale
        ) {
          this.persistLocale(pending.marketIso, pending.localeIso);
          this.clearPendingLocale();
        }
      } catch (error) {
        this.clearPendingLocale();
      }
    }

    persistLocale(marketIso, localeIso) {
      if (!marketIso || !localeIso) return;

      CookieManager.set(
        CONFIG.cookieName,
        `${marketIso}_${localeIso}`,
        CONFIG.cookieDays
      );

      try {
        window.sessionStorage.removeItem(CONFIG.dismissalCountKey);
      } catch (error) {
        // The locale cookie is authoritative if session storage is unavailable.
      }
    }

    continueWithSelection() {
      const selectedMarket = this.dataManager.getSelectedMarket();
      const selectedLocale = this.dataManager.getSelectedLocale();
      const currentMarket = this.dataManager.currentMarket;
      const currentLocale = this.dataManager.currentLocale;

      if (!selectedMarket || !selectedLocale) {
        this.close();
        return;
      }

      if (selectedMarket.iso_code !== currentMarket.iso_code) {
        this.switchMarket(selectedMarket.iso_code, selectedLocale.iso_code);
        return;
      }

      if (selectedLocale.iso_code !== currentLocale.iso_code) {
        this.switchLanguage(selectedMarket.iso_code, selectedLocale.iso_code);
        return;
      }

      this.persistLocale(selectedMarket.iso_code, selectedLocale.iso_code);
      this.close(false);
    }

    showConfirmView() {
      this.currentView = 'confirm';
      this.panelEl.querySelectorAll('.uj-localization-panel__view').forEach(v => {
        v.style.display = 'none';
      });
      this.panelEl.querySelector('[data-view="confirm"]').style.display = 'block';
      this.updateConfirmView();
    }

    showLocationView() {
      this.currentView = 'location';
      this.panelEl.querySelectorAll('.uj-localization-panel__view').forEach(v => {
        v.style.display = 'none';
      });
      this.panelEl.querySelector('[data-view="location"]').style.display = 'block';
      this.updateLocationView();
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.panelEl.style.display = 'block';
      this.showConfirmView();
    }

    close(recordDismissal = true) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.panelEl.style.display = 'none';

      if (!recordDismissal) return;

      let dismissalCount = 0;
      try {
        dismissalCount = Number(window.sessionStorage.getItem(CONFIG.dismissalCountKey)) || 0;
      } catch (error) {
        // Continue without cross-page dismissal tracking if session storage is unavailable.
      }

      dismissalCount += 1;
      if (dismissalCount >= 2) {
        const selectedMarket = this.dataManager.getSelectedMarket();
        const selectedLocale = this.dataManager.getSelectedLocale();
        if (selectedMarket && selectedLocale) {
          this.persistLocale(selectedMarket.iso_code, selectedLocale.iso_code);
        }
      } else {
        try {
          window.sessionStorage.setItem(CONFIG.dismissalCountKey, String(dismissalCount));
        } catch (error) {
          // If storage is unavailable, the popup may repeat until a cookie is set.
        }
      }
    }

    shouldShow() {
      return !CookieManager.exists(CONFIG.cookieName);
    }
  }

  // Main initialization
  document.addEventListener('DOMContentLoaded', function() {
    const browserLanguageManager = new BrowserLanguageManager();
    const dataManager = new MarketDataManager();
    const translationManager = new TranslationManager();
    const panel = new LocalizationPanel(dataManager, translationManager, browserLanguageManager);
    panel.confirmPendingLocale();

    document.querySelectorAll('.localization-panel__trigger, .localization-panel__trigger-h').forEach(trigger => {
      const countryCode = trigger.dataset.countryCode || '';
      const icon = trigger.querySelector('.localization-panel__trigger__icon');
      const language = trigger.querySelector('.localization-panel__trigger__language');

      if (countryCode.length === 2 && icon) {
        const normalizedCountryCode = countryCode.toLowerCase();
        icon.style.backgroundImage = `url("https://flagcdn.com/20x15/${normalizedCountryCode}.png")`;
        icon.setAttribute('aria-label', `${countryCode.toUpperCase()} flag`);
      }

      if (language) {
        language.setAttribute(
          'data-language-code',
          (trigger.dataset.languageCode || 'en').split('-')[0].toUpperCase()
        );
      }

      trigger.addEventListener('click', () => {
        panel.open();
        panel.showLocationView();
      });
    });

    // Make available globally for debugging and manual trigger
    window.ujLocalizationPanel = {
      open: () => panel.open(),
      close: () => panel.close(),
      panel: panel,
      dataManager: dataManager,
      translations: translationManager,
      browserLanguageManager: browserLanguageManager
    };

    // Keep the current Shopify market as the first-visit country and use the
    // browser language for the preferred locale. This preserves the existing
    // confirmation, fallback, selection, and persistence behavior without a
    // third-party geolocation request.
    const openOnFirstVisit = () => {
      if (!panel.shouldShow()) return;

      dataManager.setSuggestedLocation(
        dataManager.currentMarket.iso_code,
        browserLanguageManager.browserLanguage
      );
      panel.open();
    };

    window.setTimeout(openOnFirstVisit, CONFIG.autoShowDelay);
  });

})(window);
